import type { TourDefinition } from './types';
import { hasReviewArea, useAuthStore } from '@/stores/auth';
import { hasVisibleTarget } from '@/utils/tour/waitForElement';

/**
 * First-run orientation on the home page: what GAIA Insights is, the three
 * ways to start (search, ask the composer, run a workflow), and then a walk
 * down the sidebar in the order the work actually happens — draft, review,
 * sign, store, find again — ending on the account row.
 *
 * Purely informational. The sidebar scenes are gated on their anchor being on
 * screen rather than on permission flags: the sidebar already decides what it
 * renders, and `hasVisibleTarget` means a row the user does not have skips
 * instantly instead of burning the resolver's timeout first. It also cannot
 * drift out of step with the sidebar's own gating, which for Review is an OR
 * across three separate permissions.
 *
 * Home hosts two tours: this one is offered first, and the chat tour on a
 * later visit once this has been seen (see `tourStore.maybeAutoStart`).
 */

/**
 * The same permissions that decide whether `HomeQuickActions` has any action to
 * offer — without one of these its row renders empty, so the scene skips
 * instead of highlighting a blank strip.
 */
function canUseDropZone(): boolean {
  const permissions = useAuthStore().userPermissions;
  return hasReviewArea(permissions) || permissions.can_use_signing || permissions.can_use_playbooks;
}

export const welcomeTour: TourDefinition = {
  key: 'welcome',
  title: 'Welcome to GAIA',
  description: 'A one-minute orientation of your workspace.',
  entryRoute: 'home',
  steps: [
    {
      id: 'intro',
      route: 'home',
      title: 'Welcome to GAIA Insights 👋',
      body:
        'Your workspace for the whole contract lifecycle: review, manage, ' +
        'template, and sign, with AI doing the heavy lifting. This one-minute ' +
        'tour shows you around.',
    },
    {
      id: 'organisation',
      target: 'sidebar.workspace',
      placement: 'right-start',
      title: 'Your organisation',
      body:
        'This is the organisation you are working in. Contracts, members and ' +
        'settings all belong to it. If you are invited to another organisation, ' +
        'you switch between them here.',
    },
    {
      id: 'search',
      target: 'home.search',
      placement: 'right',
      title: 'Find anything, instantly',
      body:
        'Contracts, files, vault documents: one search across all of it. ' +
        'It is always a ⌘K away, from any page.',
    },
    {
      // The composer is the drop target now, so this scene and the chat tour's
      // "attach" scene point at the same element from different angles: here it
      // is the way in, there it is how you scope a question.
      id: 'ask',
      target: 'chat.composer',
      placement: 'top',
      title: 'Start by asking',
      body:
        'Ask anything about your contracts right here, and GAIA answers from ' +
        'your own documents. Drop a file onto the box to ask about that ' +
        'document specifically.',
    },
    {
      id: 'workflows',
      prerequisite: canUseDropZone,
      target: 'home.workflows',
      placement: 'top',
      title: 'Or jump straight into a workflow',
      body:
        'Add a contract, run a playbook, send something for signature, draft ' +
        'from a template. Pick one and GAIA takes the document from there.',
    },
    // The sidebar, top to bottom — which is also the order the work happens in.
    {
      id: 'draft',
      prerequisite: () => hasVisibleTarget('sidebar.draft'),
      target: 'sidebar.draft',
      placement: 'right',
      title: 'Create it here',
      body:
        'Draft a new document from one of your templates, or pick up a ' +
        'draft where you left off — everything you enter is saved as you go.',
    },
    {
      id: 'review',
      prerequisite: () => hasVisibleTarget('sidebar.review'),
      target: 'sidebar.review',
      placement: 'right',
      title: 'Review it here',
      body:
        'Everything that needs reading: the review board and table, plus the ' +
        'playbooks you measure contracts against and your review projects.',
    },
    {
      id: 'signing',
      prerequisite: () => hasVisibleTarget('sidebar.signing'),
      target: 'sidebar.signing',
      placement: 'right',
      title: 'Send it for signature',
      body:
        'Prepare a signing request, choose who signs where, and follow it ' +
        'until the last signature lands.',
    },
    {
      id: 'contract-management',
      prerequisite: () => hasVisibleTarget('sidebar.contract-management'),
      target: 'sidebar.contract-management',
      placement: 'right',
      title: 'Then it lives here',
      body:
        'Signed contracts go into contract management: your own views ' +
        'over them and the deadline calendar.',
    },
    {
      id: 'vault',
      prerequisite: () => hasVisibleTarget('sidebar.vault'),
      target: 'sidebar.vault',
      placement: 'right',
      title: 'And everything is in the vault',
      body:
        'Every contract and every file you or GAIA ever touched lives in the ' +
        'Contract Vault. If you are looking for a document and do not know ' +
        'where it went, it is in here.',
    },
    {
      id: 'account',
      prerequisite: () => hasVisibleTarget('sidebar.account'),
      target: 'sidebar.account',
      placement: 'right-end',
      title: 'Your account, and the settings',
      body: (): string =>
        useAuthStore().userPermissions.is_org_admin
          ? 'The cog opens your profile and the whole organisation: users, ' +
            'permissions, templating, memories and integrations. Every feature ' +
            'also has a guided demo like this one — replay any of them from ' +
            'Help in here.'
          : 'The cog opens your profile, your credits and the changelog. Every ' +
            'feature also has a guided demo like this one — replay any of them ' +
            'from Help in here.',
    },
    {
      id: 'finale',
      title: 'You are ready to go',
      body:
        'Explore at your own pace, or pick any feature below and its demo ' +
        'will walk you through, just like this one.',
    },
  ],
};
